import React, { useState } from 'react';
import { useArticles } from '../hooks/useArticles';

const AddUrlForm = ({ onSuccess, className = '' }) => {
  const [url, setUrl] = useState('');
  const [isExpanded, setIsExpanded] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [bulkResults, setBulkResults] = useState(null);
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);

  const { addArticle, addArticlesBulk, isCreating, error, clearError } = useArticles();

  const validateUrl = (urlString) => {
    try {
      const urlObj = new URL(urlString);
      return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
    } catch {
      return false;
    }
  };

  const parseUrls = (input) => {
    // Split by newlines and filter empty lines
    return input
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0);
  };

  const validateUrls = (urls) => {
    const results = urls.map(url => ({
      url,
      valid: validateUrl(url)
    }));
    return results;
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setUrl(value);

    // Clear errors and results when user starts typing
    if (error) clearError();
    if (validationError) setValidationError('');
    if (bulkResults) setBulkResults(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!url.trim()) {
      setValidationError('Please enter a URL');
      return;
    }

    const urls = parseUrls(url);

    // Single URL case - use existing flow
    if (urls.length === 1) {
      if (!validateUrl(urls[0])) {
        setValidationError('Please enter a valid URL (must start with http:// or https://)');
        return;
      }

      try {
        const article = await addArticle({ url: urls[0] });

        // Show success animation
        setShowSuccessAnimation(true);
        setTimeout(() => setShowSuccessAnimation(false), 1000);

        // Reset form
        setUrl('');
        setIsExpanded(false);
        setValidationError('');

        // Notify parent component
        if (onSuccess) {
          onSuccess(article);
        }
      } catch (error) {
        console.error('Error adding article:', error);
      }
      return;
    }

    // Multiple URLs - validate and use bulk endpoint
    const validationResults = validateUrls(urls);
    const validUrls = validationResults.filter(r => r.valid).map(r => r.url);
    const invalidUrls = validationResults.filter(r => !r.valid).map(r => r.url);

    if (validUrls.length === 0) {
      setValidationError('No valid URLs found. URLs must start with http:// or https://');
      return;
    }

    try {
      const response = await addArticlesBulk(validUrls);

      // Store results for display
      setBulkResults({
        ...response,
        invalidUrls
      });

      // Show success animation
      if (response.successful > 0) {
        setShowSuccessAnimation(true);
        setTimeout(() => setShowSuccessAnimation(false), 1000);
      }

      // Reset form after a delay to show results
      setTimeout(() => {
        setUrl('');
        setIsExpanded(false);
        setValidationError('');
        setBulkResults(null);

        // Notify parent component with first successful article
        if (onSuccess && response.successful > 0) {
          const firstSuccess = response.results.find(r => r.success);
          if (firstSuccess) {
            onSuccess(firstSuccess.article);
          }
        }
      }, 3000);
    } catch (error) {
      console.error('Error adding articles in bulk:', error);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsExpanded(false);
      setUrl('');
      setValidationError('');
      if (error) clearError();
    }
  };

  const handleFocus = () => {
    setIsExpanded(true);
  };

  const handleBlur = () => {
    // Only collapse if we're not submitting and there's no content
    if (!isCreating && !url.trim() && !validationError && !error && !bulkResults) {
      setIsExpanded(false);
    }
  };

  // Count URLs in input
  const urlCount = url.trim() ? parseUrls(url).length : 0;

  return (
    <div className={`w-full ${className}`}>
      <form onSubmit={handleSubmit} className="w-full">
        <div className="relative">
          <div className={`transition-all duration-300 ease-bounce-in ${
            isExpanded
              ? 'card-clay-static p-5'
              : 'bg-white rounded-clay-lg border-3 border-primary-200 shadow-clay-sm hover:border-primary-300 hover:shadow-clay'
          } ${showSuccessAnimation ? 'animate-celebrate' : ''}`}>
            <div className="flex items-start space-x-3">
              {/* Link icon with animation */}
              <div className={`flex-shrink-0 pt-2.5 transition-all duration-300 ${
                isExpanded ? 'text-primary-500 scale-110' : 'text-gray-400'
              }`}>
                <svg
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2.5}
                    d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                  />
                </svg>
              </div>

              <div className="flex-1">
                {isExpanded ? (
                  <textarea
                    placeholder="Paste URLs to save articles (one per line)..."
                    value={url}
                    onChange={handleInputChange}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    onKeyDown={handleKeyDown}
                    disabled={isCreating}
                    rows={urlCount > 1 ? Math.min(urlCount, 5) : 3}
                    className={`w-full px-0 py-2 bg-transparent border-none focus:outline-none focus:ring-0
                      placeholder-gray-400 text-gray-900 font-body resize-none text-base ${
                      isCreating ? 'cursor-not-allowed opacity-50' : ''
                    }`}
                  />
                ) : (
                  <input
                    type="text"
                    placeholder="Paste a URL to save an article..."
                    value={url}
                    onChange={handleInputChange}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    onKeyDown={handleKeyDown}
                    disabled={isCreating}
                    className={`w-full px-0 py-2.5 bg-transparent border-none focus:outline-none focus:ring-0
                      placeholder-gray-400 text-gray-900 font-body text-base ${
                      isCreating ? 'cursor-not-allowed opacity-50' : ''
                    }`}
                  />
                )}
              </div>

              {isExpanded && (
                <div className="flex items-center space-x-2 pt-1">
                  <button
                    type="submit"
                    disabled={isCreating || !url.trim()}
                    className="btn-clay-accent btn-clay-sm flex items-center gap-2 whitespace-nowrap"
                  >
                    {isCreating ? (
                      <>
                        <div className="spinner-fun-sm w-4 h-4"></div>
                        <span>Saving...</span>
                      </>
                    ) : urlCount > 1 ? (
                      <>
                        <span>Save {urlCount}</span>
                        <span className="text-lg">+</span>
                      </>
                    ) : (
                      <>
                        <span>Save</span>
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>

            {/* Bulk results display */}
            {isExpanded && bulkResults && (
              <div className="mt-4 p-4 bg-primary-50 border-2 border-primary-200 rounded-clay">
                <div className="text-sm font-display font-bold text-primary-800 mb-2 flex items-center gap-2">
                  <span>Bulk Save Results</span>
                  <span className="text-lg">
                    <span role="img" aria-label="sparkles">✨</span>
                  </span>
                </div>
                <div className="text-sm text-primary-700 space-y-1.5">
                  {bulkResults.successful > 0 && (
                    <div className="flex items-center gap-2">
                      <span className="text-fun-mint">✓</span>
                      <span>{bulkResults.successful} saved successfully!</span>
                    </div>
                  )}
                  {bulkResults.duplicates > 0 && (
                    <div className="flex items-center gap-2">
                      <span className="text-accent-500">⚠</span>
                      <span>{bulkResults.duplicates} already in library</span>
                    </div>
                  )}
                  {bulkResults.failed > 0 && (
                    <div className="flex items-center gap-2">
                      <span className="text-fun-coral">✗</span>
                      <span>{bulkResults.failed} failed</span>
                    </div>
                  )}
                  {bulkResults.invalidUrls && bulkResults.invalidUrls.length > 0 && (
                    <div className="flex items-center gap-2">
                      <span className="text-fun-coral">✗</span>
                      <span>{bulkResults.invalidUrls.length} invalid URLs</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Error display */}
            {isExpanded && (validationError || error) && (
              <div className="mt-3 p-3 bg-rose-50 border-2 border-rose-200 rounded-clay">
                <div className="flex items-center gap-2 text-sm text-rose-700">
                  <span className="text-lg">
                    <span role="img" aria-label="warning">😅</span>
                  </span>
                  <span>{validationError || error}</span>
                </div>
              </div>
            )}

            {/* Helper text */}
            {isExpanded && !validationError && !error && !bulkResults && urlCount > 0 && (
              <div className="mt-3 flex items-center gap-2 text-xs text-gray-500">
                {urlCount > 1 ? (
                  <>
                    <span className="badge-fun-secondary text-xs py-0.5">{urlCount} URLs</span>
                    <span>Press Escape to cancel</span>
                  </>
                ) : (
                  <>
                    <span>Press Escape to cancel</span>
                    <span className="text-primary-500 font-medium">• Processing happens in the background</span>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

export default AddUrlForm;
